import {createElement, useCallback, useMemo, type ReactNode} from "react"

import {localize, useLocalize} from "@agenta/i18n"
import {
    AGENTS_SIDEBAR_KEY,
    buildHelpDocsNavItem,
    defineSidebarEntity,
    resolveChildren,
    SESSIONS_SIDEBAR_KEY,
    SKILLS_SIDEBAR_KEY,
    sidebarSessionToggledGroupsAtomFamily,
    sidebarSessionGroupKey,
    sidebarSessionGroupsAtomFamily,
    loadMoreSidebarSessionsAtomFamily,
    RELEASES,
    SIDEBAR_UNBOUNDED,
    sidebarSessionsListAtomFamily,
    setSidebarSessionFilterDefaults,
    withEntityGroups,
    type SessionSidebarRef,
    type SidebarConfig,
    type SidebarEntityRef,
} from "@agenta/navigation"
import {buildReleaseNavItems, SessionFilterMenu, SessionSearchButton} from "@agenta/navigation-ui"
import {SessionRowActions, useSessionActions, useSessionRowChrome} from "@agenta/sessions-ui"
import {getShortcut, shortcutAria, shortcutFaces} from "@agenta/shared/utils"
import {useIsMacPlatform} from "@agenta/ui/shortcuts"
import {Spinner} from "@agenta/ui/ui"
import {
    ChartLineUpIcon,
    ChatsCircleIcon,
    CircleIcon,
    GearIcon,
    GithubLogoIcon,
    GraduationCapIcon,
    HouseIcon,
    KeyboardIcon,
    LightningIcon,
    QuestionIcon,
    RobotIcon,
    ScrollIcon,
    SlackLogoIcon,
    SquaresFourIcon,
} from "@phosphor-icons/react"
import {atom, useAtomValue, useSetAtom} from "jotai"
import {unwrap} from "jotai/utils"

import {agentEntryHref} from "@/features/agents/agentEntry"
import {useLeaveSession} from "@/features/chat/useLeaveSession"
import {startBlankSession} from "@/features/chat/useStartBlankSession"
import {whatsNewAtom} from "@/features/education/whatsNewAtom"

import {AgentsNavAction} from "./AgentsNavAction"
import {AgentsQuickPanel} from "./AgentsQuickPanel"
import {useSyncLocalSessionRefs} from "./localSessionRefs"

/** The drawer's scope id — its open-groups persistence bucket. */
export const MOBILE_NAV_SCOPE_ID = "mobile-main"
// By agent, not the shared flat default: the rail's "+" lives on agent headings, and a heading
// that is a date or a status names no agent to start a session with. Reset comes back here.
setSidebarSessionFilterDefaults(MOBILE_NAV_SCOPE_ID, {groupBy: "agent"})
/** The registry entry for ⌘K / Ctrl+K, which `MobileCommandPalette` binds. */
const PALETTE_SHORTCUT_ID = "palette.open"

/** Flip to show the Observability rail entry again — the screen and its route are untouched. */
const SHOW_OBSERVABILITY = false

/**
 * Mobile's registration over the SHARED machinery: same gated sessions source, same
 * pinned-first ordering, mobile's own child routes.
 */
export const mobileSessionsEntity = defineSidebarEntity<SessionSidebarRef>(
    MOBILE_NAV_SCOPE_ID,
    SESSIONS_SIDEBAR_KEY,
    {
        kind: "app",
        icon: createElement(ChatsCircleIcon, {size: 14}),
        // Its OWN scope: the source reads that scope's filters, so the desktop rail's filters
        // cannot narrow this drawer. Mobile has no filter UI, so this scope keeps the defaults.
        listAtom: sidebarSessionsListAtomFamily(MOBILE_NAV_SCOPE_ID),
        getLabel: (session) => session.name || localize("com_nav_untitled_session"),
        childPath: (session) => `/sessions/${session.sessionId}`,
        // A catalog key: resolved where the placeholder renders.
        emptyLabel: "com_nav_no_sessions_yet",
        // No caret on this row to protect, and "No sessions yet" under a heading that already
        // says Sessions is the same sentence twice.
        hideWhenEmpty: true,
        // No "Show all" row: the group's own "Sessions" row already links to the full list, and
        // the headings make a trailing overflow link read as one more session.
        // No pin glyph: pinned rows sit under their own heading, which says it once.
        // Amber for a session blocked on you. `--ag-run-status-warning` rather than
        // `colorWarning`: the semantic token's light step is a muddy #8a6400 that reads as
        // disabled at 8px, and this one is the palette's bright amber in BOTH themes.
        getIcon: (session) => {
            // State wins the glyph while a turn is live; otherwise the SHAPE says the type — a
            // bolt for a trigger run, a dot for a chat — and the colour still carries the gate.
            const amber = session.waiting ? "text-[var(--ag-run-status-warning)]" : undefined
            const live = session.waiting || session.alive
            if (session.running) return createElement(Spinner, {className: "size-3"})
            if (session.isAutomation)
                return createElement(LightningIcon, {
                    size: 12,
                    // Fill means LIVE on both glyphs; the bolt shape alone says automation.
                    weight: live ? "fill" : "regular",
                    className: amber,
                })
            return createElement(CircleIcon, {
                size: 8,
                weight: live ? "fill" : "regular",
                className: amber,
            })
        },
        // Archived rows read as second-class: same row, dimmed. The archived view is the only
        // place they appear, so this says WHICH list you are looking at as much as which row.
        getRowClassName: (session) => (session.archived ? "opacity-60" : undefined),
        // Grouped by owning agent, pins in their own heading on top (#6125).
        getGroupKey: sidebarSessionGroupKey,
        groupsAtom: sidebarSessionGroupsAtomFamily(MOBILE_NAV_SCOPE_ID),
        // An agent heading's "+" opens a blank session with that agent — the same start every
        // other "+" in the app makes. The session id is minted on the click, so the anchor's
        // own href is the agent entry link: what a long-press / open-in-new-tab lands on.
        groupAdd: (group, projectURL) =>
            group.agentId
                ? {
                      link: agentEntryHref(projectURL, group.agentId),
                      label: localize("com_nav_new_session_with", {name: group.label}),
                      onClick: (event) => {
                          event.preventDefault()
                          startBlankSession(projectURL, group.agentId!)
                      },
                  }
                : undefined,
        toggleGroupAtom: sidebarSessionToggledGroupsAtomFamily(MOBILE_NAV_SCOPE_ID),
        // No visible cap: the rail renders every row it fetched, so nothing is dropped between
        // the request and the render. The server window is the only bound.
        maxItems: SIDEBAR_UNBOUNDED,
    },
)

/**
 * The rail's nav model — the same keys, order and icon sizes the desktop rail uses, narrowed
 * to the screens mobile has. Entries appear here as their screens land; nothing is hidden by
 * forking a component.
 */
export const useMobileNavItems = (projectURL: string): SidebarConfig[] => {
    const t = useLocalize()
    // Sessions this client created ride the shared local seam until the server lists them.
    useSyncLocalSessionRefs(MOBILE_NAV_SCOPE_ID)
    const rawSource = useAtomValue(mobileSessionsEntity.activeSourceAtom)
    const loadMoreSessions = useSetAtom(loadMoreSidebarSessionsAtomFamily(MOBILE_NAV_SCOPE_ID))
    const groups = useAtomValue(sidebarSessionGroupsAtomFamily(MOBILE_NAV_SCOPE_ID))
    // MEMOIZED, and load-bearing: `withEntityGroups` spreads into a new object, so an unmemoized
    // call changes identity on every render — which busts the memo below, re-buckets every row,
    // and hands `NavMenu` a new items array that defeats its own memo.
    const source = useMemo(() => withEntityGroups(rawSource, groups), [rawSource, groups])
    // Resolved ONCE for the rail, not once per row: the verbs do not differ by session.
    // Archiving or deleting the session on screen has to move you off it, from here too.
    const chrome = useSessionRowChrome(useSessionActions({onRemoved: useLeaveSession(projectURL)}))
    // Platform read in an effect: the server has none, and a guess mismatches on hydration.
    const mac = useIsMacPlatform()
    const paletteShortcut = useMemo(() => {
        const shortcut = getShortcut(PALETTE_SHORTCUT_ID)
        return shortcut
            ? {faces: shortcutFaces(shortcut, mac), aria: shortcutAria(PALETTE_SHORTCUT_ID)}
            : undefined
    }, [mac])
    const wrapSessionRow = useCallback(
        (ref: SidebarEntityRef, node: ReactNode) =>
            createElement(SessionRowActions, {
                session: ref as SessionSidebarRef,
                chrome,
                children: node,
            }),
        [chrome],
    )

    return useMemo(
        () => [
            {
                key: "mobile-home",
                title: t("com_nav_home"),
                icon: createElement(HouseIcon, {size: 16}),
                link: `${projectURL}/apps`,
            },
            {
                key: AGENTS_SIDEBAR_KEY,
                title: t("com_nav_agents"),
                icon: createElement(RobotIcon, {size: 16}),
                link: `${projectURL}/agents`,
                // Start or open an agent without leaving the page: a "+" on the row, the same
                // panel as a hover flyout on the collapsed rail.
                rowAction: createElement(AgentsNavAction, {base: projectURL}),
                hoverFlyout: (close: () => void, hold: (held: boolean) => void) =>
                    createElement(AgentsQuickPanel, {
                        base: projectURL,
                        onDone: close,
                        onBusyChange: hold,
                        autoFocusSearch: false,
                    }),
            },
            // After Agents: an automation is something an agent has, and Skills is what it uses.
            {
                key: "automations",
                title: t("com_nav_automations"),
                icon: createElement(LightningIcon, {size: 16}),
                link: `${projectURL}/automations`,
            },
            {
                key: SKILLS_SIDEBAR_KEY,
                title: t("com_nav_skills"),
                icon: createElement(GraduationCapIcon, {size: 16}),
                link: `${projectURL}/skills`,
            },
            {
                key: SESSIONS_SIDEBAR_KEY,
                title: t("com_nav_sessions"),
                icon: createElement(ChatsCircleIcon, {size: 16}),
                link: `${projectURL}/sessions`,
                hideChildrenWhenCollapsed: true,
                // No collapse caret here: the filter control is this group's affordance, and the
                // rows are grouped and individually collapsible already.
                alwaysOpen: true,
                // The rail does not scroll; THIS group does. Sessions is the only list that grows
                // without bound, so Observability (and whatever lands after it) stays on screen.
                scrollChildren: true,
                onReachEnd: loadMoreSessions,
                // Search reaches past what the rail lists; the filter narrows it. Same pair,
                // same order, as the desktop rail.
                groupAction: createElement(
                    "span",
                    {className: "flex items-center gap-0.5"},
                    createElement(SessionSearchButton, {
                        label: t("com_ui_search"),
                        shortcut: paletteShortcut,
                    }),
                    createElement(SessionFilterMenu, {scopeId: MOBILE_NAV_SCOPE_ID}),
                ),
                submenu: resolveChildren(
                    mobileSessionsEntity,
                    source,
                    projectURL,
                    undefined,
                    undefined,
                    wrapSessionRow,
                ),
            },
            // Observability is hidden from the rail for now; the screen and its route still work.
            ...(SHOW_OBSERVABILITY
                ? [
                      {
                          key: "mobile-observability",
                          title: t("com_nav_observability"),
                          icon: createElement(ChartLineUpIcon, {size: 16}),
                          link: `${projectURL}/observability`,
                      },
                  ]
                : []),
        ],
        // `t` changes with the language, so the rows (and the localized placeholders inside
        // `resolveChildren`) rebuild on a switch.
        [loadMoreSessions, source, projectURL, wrapSessionRow, paletteShortcut, t],
    )
}

// Lazy-load package.json so its version stays out of the initial bundle.
// `unwrap` yields undefined until the import settles, which is all the suffix below needs.
const versionAtom = unwrap(atom(async () => (await import("../../../package.json")).version))

export const useMobileVersion = () => useAtomValue(versionAtom)

/** The pinned bottom entries: Templates and Settings, both dropped inside the settings scope. */
export const useMobileBottomNavItems = (
    projectURL: string,
    {includeSettingsLink = true}: {includeSettingsLink?: boolean} = {},
): SidebarConfig[] => {
    const t = useLocalize()
    const version = useMobileVersion()
    return useMemo(
        () =>
            // The settings scope drops them: the rail IS settings there.
            includeSettingsLink
                ? [
                      {
                          key: "mobile-templates",
                          title: t("com_nav_templates"),
                          icon: createElement(SquaresFourIcon, {size: 16}),
                          link: `${projectURL}/templates`,
                      },
                      {
                          key: "mobile-settings",
                          title: t("com_nav_settings"),
                          icon: createElement(GearIcon, {size: 16}),
                          link: `${projectURL}/settings`,
                          suffix: version
                              ? createElement(
                                    "span",
                                    {className: "text-[10px] leading-none text-colorTextTertiary"},
                                    `v${version}`,
                                )
                              : undefined,
                      },
                  ]
                : [],
        [includeSettingsLink, projectURL, t, version],
    )
}

/**
 * Help & Docs as an item the rail renders as an icon button beside the project switcher, the
 * same place the desktop rail puts it. It is a menu you reach for, not a place in the product,
 * and the row it held was the widest thing in the rail's footer.
 */
export const useMobileHelpItem = ({
    onOpenShortcuts,
}: {onOpenShortcuts?: () => void} = {}): SidebarConfig => {
    const t = useLocalize()
    const version = useMobileVersion()
    const openWhatsNew = useSetAtom(whatsNewAtom)

    return useMemo(
        () =>
            buildHelpDocsNavItem({
                icons: {
                    help: createElement(QuestionIcon, {size: 16}),
                    docs: createElement(ScrollIcon, {size: 14}),
                    github: createElement(GithubLogoIcon, {size: 14}),
                    slack: createElement(SlackLogoIcon, {size: 14}),
                },
                extraItems: [
                    {
                        key: "keyboard-shortcuts",
                        title: t("com_nav_keyboard_shortcuts"),
                        icon: createElement(KeyboardIcon, {size: 14}),
                        isHidden: !onOpenShortcuts,
                        onClick: () => onOpenShortcuts?.(),
                        // The rule between the destinations and the release list.
                        divider: true,
                    },
                    // Release rows reopen the what's-new dialog; "View all releases" links out.
                    ...buildReleaseNavItems(version).map((item) =>
                        RELEASES.some((release) => release.id === item.key)
                            ? {
                                  ...item,
                                  link: undefined,
                                  onClick: () => openWhatsNew({releaseId: item.key}),
                              }
                            : item,
                    ),
                ],
            }),
        // `t` rebuilds the menu on a language switch: the shared builders localize as they run.
        [onOpenShortcuts, openWhatsNew, t, version],
    )
}
